import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import GenericError, {
  GenericErrorType,
} from '@provider-portal/menu-common/components/GenericError/GenericError';
import { format } from 'date-fns';

import { PromotionDetails } from '../../types';

export enum TypeOfStat {
  PROMOTION_TYPE,
  DATE_START,
  DATE_END,
  ORDERS,
  CUSTOMERS,
  SALES,
  TOTAL_SPEND,
  DATES,
}

export interface PromotionTableFCProps {
  strings: {
    tablePromotionFields: {
      promotionType: string;
      startDate: string;
      endDate: string;
      dates: string;
      orders: string;
      customers: string;
      newUsers: string;
      sales: string;
      gmvImpact: string;
      totalSpend: string;
    };
    messages: {
      errorFetchingAssignedPromotions: string;
      pagerShowRowsFrom: string;
    };
  };
  tableName: string;
  promotionDetailList: PromotionDetails[];
  promotionClicked: (id: number) => void;
  showPagination?: boolean;
  rowsPerPage?: number;
}
export interface PromotionTableHeaderProp {
  header: {
    promotionType: string;
    startDate: string;
    endDate: string;
    dates: string;
    orders: string;
    customers: string;
    newUsers: string;
    sales: string;
    gmvImpact: string;
    totalSpend: string;
  };
}

const styles = {
  promotionTable: {
    backgroundColor: 'colors.white',
    mt: 2,
    pt: 2,
  },

  promotionTableItem: {
    cursor: 'pointer',
  },
  typeHeader: {
    width: '30%',
  },
  datesWidth: {
    minWidth: '100px',
    width: '15%',
  },
};

const DATE_SEPARATOR = '$$';

export interface PromotionsTableProp {
  promotionDetails: PromotionDetails[];
  onClickPromotionHandler: (id: number) => void;
  errorMessage: string;
}

interface PromotionDetailsProps {
  promotionsList: PromotionDetails[];
  onSelectedPromotion: (promotionId: number) => void;
}

interface PromotionProp {
  promotionDetails: PromotionDetails;
  onSelectedPromotion: (id: number) => void;
}

interface FormatCellProps {
  typeOfStat?: TypeOfStat;
  cellInformation?: string | null;
  isCampaignUsed?: boolean;
}

interface ListStatsProp {
  stat?: string | null;
  typeStat?: TypeOfStat;
}

const ListNumbers: React.FC<ListStatsProp> = memo(({ stat, typeStat }) => {
  if (!stat || stat === '0') {
    return <>-</>;
  }

  if (typeStat === TypeOfStat.ORDERS || typeStat === TypeOfStat.CUSTOMERS) {
    return <> {Math.floor(Number(stat))}</>;
  }

  if (typeStat === TypeOfStat.SALES) {
    return <> {stat}</>;
  }
  if (typeStat === TypeOfStat.DATES) {
    const dates = stat?.split(DATE_SEPARATOR, 2);
    return (
      <>
        {' '}
        <div> {dates[0]} –</div> <div> {dates[1]} </div>{' '}
      </>
    );
  }

  return <>{stat}</>;
});

const FormatCellWriting = ({ typeOfStat, cellInformation, isCampaignUsed }: FormatCellProps) => {
  const props = { typeOfStat, isCampaignUsed, cellInformation };
  const customTypography = useMemo(() => {
    return {
      color: !props.isCampaignUsed ? 'colors.neutral700' : 'colors.neutral900',
      pl: 0,
    };
  }, [props.isCampaignUsed]);

  return (
    <TableCell>
      <Box mr={props.typeOfStat === TypeOfStat.ORDERS ? 4 : 0} textAlign="left">
        <Typography variant="bodyM" sx={customTypography}>
          <ListNumbers typeStat={typeOfStat} stat={cellInformation} />
        </Typography>
      </Box>
    </TableCell>
  );
};

const ListEveryPromotion: React.FC<PromotionProp> = memo(
  ({ promotionDetails, onSelectedPromotion }) => {
    const onSelectHandler = useCallback(() => {
      onSelectedPromotion(promotionDetails.id);
    }, [promotionDetails.id, onSelectedPromotion]);
    const isCampaignUsed = !!(
      promotionDetails.numberOrders ||
      promotionDetails.numberCustomers ||
      promotionDetails.sales
    );

    return (
      <TableRow
        key={promotionDetails.promotionName}
        onClick={onSelectHandler}
        sx={styles.promotionTableItem}
      >
        <FormatCellWriting
          typeOfStat={TypeOfStat.PROMOTION_TYPE}
          isCampaignUsed={isCampaignUsed}
          cellInformation={promotionDetails.promotionName}
        />
        <FormatCellWriting
          cellInformation={`${format(
            promotionDetails.startDate,
            'MMM dd, yyyy',
          )}${DATE_SEPARATOR}${format(promotionDetails.endDate, 'MMM dd, yyyy')}`}
          isCampaignUsed={isCampaignUsed}
          typeOfStat={TypeOfStat.DATES}
        />
        <FormatCellWriting
          cellInformation={promotionDetails.numberOrders}
          isCampaignUsed={isCampaignUsed}
          typeOfStat={TypeOfStat.ORDERS}
        />
        <FormatCellWriting
          cellInformation={promotionDetails.numberCustomers}
          isCampaignUsed={isCampaignUsed}
          typeOfStat={TypeOfStat.CUSTOMERS}
        />
        <FormatCellWriting
          cellInformation={promotionDetails.sales}
          isCampaignUsed={isCampaignUsed}
          typeOfStat={TypeOfStat.SALES}
        />
        <FormatCellWriting
          cellInformation={promotionDetails.newUsers ?? null}
          isCampaignUsed={isCampaignUsed}
          typeOfStat={TypeOfStat.CUSTOMERS}
        />
        <FormatCellWriting
          cellInformation={promotionDetails.gmvImpact ?? null}
          isCampaignUsed={isCampaignUsed}
          typeOfStat={TypeOfStat.SALES}
        />
        <FormatCellWriting
          cellInformation={promotionDetails.totalSpend ?? null}
          isCampaignUsed={isCampaignUsed}
          typeOfStat={TypeOfStat.TOTAL_SPEND}
        />
      </TableRow>
    );
  },
);

const ListPromotionsDetails: React.FC<PromotionDetailsProps> = memo(
  ({ promotionsList, onSelectedPromotion }) => {
    if (!promotionsList || !promotionsList.length) {
      return null;
    }
    return (
      <>
        {promotionsList.map((row) => (
          <ListEveryPromotion
            key={row.id}
            promotionDetails={row}
            onSelectedPromotion={onSelectedPromotion}
          />
        ))}
      </>
    );
  },
);

const PromotionsTableBody: React.FC<PromotionsTableProp> = memo(
  ({ promotionDetails, onClickPromotionHandler, errorMessage }) => {
    if (!promotionDetails || !Array.isArray(promotionDetails)) {
      return <GenericError type={GenericErrorType.error} intlIdTitle={errorMessage} />;
    }
    return (
      <ListPromotionsDetails
        promotionsList={promotionDetails}
        onSelectedPromotion={onClickPromotionHandler}
      />
    );
  },
);

const PromotionsTableHeader: React.FC<PromotionTableHeaderProp> = memo(({ header }) => {
  return (
    <TableRow>
      <TableCell align="left" sx={styles.typeHeader}>
        <Typography variant="subtitleS">{header.promotionType}</Typography>
      </TableCell>
      <TableCell align="left" sx={styles.datesWidth}>
        <Typography variant="subtitleS">{header.dates}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subtitleS">{header.orders}</Typography>
      </TableCell>
      <TableCell align="left">
        <Typography variant="subtitleS">{header.customers}</Typography>
      </TableCell>
      <TableCell align="left">
        <Typography variant="subtitleS">{header.sales}</Typography>
      </TableCell>
      <TableCell align="left">
        <Typography variant="subtitleS">{header.newUsers}</Typography>
      </TableCell>
      <TableCell align="left">
        <Typography variant="subtitleS">{header.gmvImpact}</Typography>
      </TableCell>
      <TableCell align="left">
        <Typography variant="subtitleS">{header.totalSpend}</Typography>
      </TableCell>
    </TableRow>
  );
});

export const PromotionTableFC: React.FC<PromotionTableFCProps> = memo(
  ({
    promotionDetailList,
    strings,
    promotionClicked,
    tableName,
    showPagination,
    rowsPerPage = 10,
  }) => {
    const [page, setPage] = useState<number>(0);
    const intl = useIntl();
    const [promotionsToShow, setPromotionsToShow] = useState<PromotionDetails[]>([]);

    useEffect(() => {
      if (!showPagination) {
        setPromotionsToShow(promotionDetailList);
      } else if (rowsPerPage > 0) {
        setPromotionsToShow(
          promotionDetailList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        );
      }
    }, [page, promotionDetailList, rowsPerPage, showPagination]);

    const handleChangePage = useCallback((_event: unknown, newPage: number) => {
      setPage(newPage);
    }, []);

    const showPagerLabel = useCallback(
      ({ from, to, count }: { from: number; to: number; count: number }) => {
        return intl.formatMessage(
          { id: 'components.assignedPromotions.messages.pagerShowRowsFrom' },
          {
            from: from.toString(),
            to: to.toString(),
            count: count.toString(),
          },
        );
      },
      [intl],
    );

    const historyPromotionsTableHeader = useMemo(() => {
      return <PromotionsTableHeader header={strings.tablePromotionFields} />;
    }, [strings.tablePromotionFields]);

    const historyPromotionsTableBody = useMemo(() => {
      return (
        <PromotionsTableBody
          promotionDetails={promotionsToShow}
          onClickPromotionHandler={promotionClicked}
          errorMessage={strings.messages.errorFetchingAssignedPromotions}
        />
      );
    }, [promotionsToShow, promotionClicked, strings.messages.errorFetchingAssignedPromotions]);

    return (
      <Box mt={5}>
        <Typography variant="h5" alignSelf="center">
          {tableName}
        </Typography>

        <TableContainer sx={styles.promotionTable}>
          <Table aria-labelledby="tableTitle" aria-label="promotions table">
            <TableHead>{historyPromotionsTableHeader}</TableHead>
            <TableBody>{historyPromotionsTableBody}</TableBody>
            {showPagination && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    colSpan={8}
                    component="td"
                    count={promotionDetailList.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    rowsPerPageOptions={[]}
                    onPageChange={handleChangePage}
                    labelDisplayedRows={showPagerLabel}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Box>
    );
  },
);
